import React, { useState, useEffect, useRef } from "react";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import {
  StatCard,
  SectionCard,
  ErrorMessage,
  SimpleTable,
  Badge,
  formatNumber,
} from "./StatComponents";

const USER_TYPE_LABELS = {
  2: "Veli",
  10: "Personel",
  50: "Sürücü",
  1000: "Admin",
};

const userTypeLabel = (usertype) => USER_TYPE_LABELS[String(usertype)] || String(usertype);

const CUTOFF_STORAGE_KEY = "admin-statistics-migration-cutoff";
const DEFAULT_CUTOFF = "1.0.142";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const resolveInitialCutoff = () => {
  try {
    return localStorage.getItem(CUTOFF_STORAGE_KEY) || DEFAULT_CUTOFF;
  } catch {
    return DEFAULT_CUTOFF;
  }
};

const MigrationSection = () => {
  const [cutoff, setCutoff] = useState(resolveInitialCutoff);
  const [inputValue, setInputValue] = useState(resolveInitialCutoff);
  const { data, isLoading, error, load } = useStatsFetch("", false);
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    load(`/admin/statistics/legacy-migration?cutoff=${encodeURIComponent(cutoff)}`);
  }, [cutoff, load]);

  const runAnalysis = () => {
    const trimmed = inputValue.trim();
    if (!/^\d+(\.\d+)*$/.test(trimmed)) {
      if (window?.toast?.error) window.toast.error("Geçerli bir sürüm girin (örn. 1.0.142).");
      return;
    }
    setCutoff(trimmed);
    try {
      localStorage.setItem(CUTOFF_STORAGE_KEY, trimmed);
    } catch {}
    load(`/admin/statistics/legacy-migration?cutoff=${encodeURIComponent(trimmed)}`);
  };

  const mobileSummary = data?.mobile?.summary;
  const legacySummary = data?.legacyEndpoint?.summary;

  const blockerCount = (mobileSummary?.activeLast7 || 0) + (legacySummary?.activeLast7 || 0);
  const isReadyToShutdown = data && blockerCount === 0;

  const usersCell = (row) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      {row.users.length === 0 ? (
        <span style={{ color: "#999", fontSize: "12px" }}>(eşleşen kullanıcı yok)</span>
      ) : (
        row.users.map((u, i) => (
          <span key={i} style={{ fontSize: "12px", display: "flex", gap: "6px", alignItems: "center" }}>
            <Badge>{userTypeLabel(u.usertype)}</Badge>
            <span>{u.name}</span>
            <span style={{ color: "#999" }}>{u.company}</span>
          </span>
        ))
      )}
    </div>
  );

  return (
    <div>
      <SectionCard title="Geçiş Sürümü (Cutoff)">
        <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
          Mobil tarafın yeni backend'e geçtiği sürümü girin. Bu sürümün <strong>altında</strong> kalan cihazlar eski
          backend'i kullanıyor demektir. Aşağıdaki iki sinyal de sıfıra inince eski backend güvenle kapatılabilir.
        </p>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runAnalysis()}
            placeholder="örn. 1.0.142"
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
              width: "160px",
            }}
          />
          <button
            onClick={runAnalysis}
            disabled={isLoading}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              padding: "8px 18px",
              borderRadius: "4px",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? "Analiz ediliyor..." : "Analiz Et"}
          </button>
        </div>
      </SectionCard>

      <ErrorMessage message={error} />

      {isLoading && !data ? (
        <MySkeleton lineCount={8} />
      ) : data ? (
        <>
          <div
            style={{
              padding: "14px 18px",
              borderRadius: "8px",
              marginBottom: "20px",
              background: isReadyToShutdown ? "#e8f5e9" : "#ffebee",
              border: `1px solid ${isReadyToShutdown ? "#81c784" : "#ef9a9a"}`,
              fontSize: "14px",
              color: isReadyToShutdown ? "#1b5e20" : "#b71c1c",
            }}
          >
            {isReadyToShutdown ? (
              <>
                <strong>Eski backend kapatılabilir görünüyor.</strong> Son 7 günde {data.cutoff} altı sürümle görülen
                cihaz yok ve eski uç noktalara yazan cihaz yok.
              </>
            ) : (
              <>
                <strong>Henüz kapatma.</strong> Son 7 günde hâlâ aktif {formatNumber(blockerCount)} sinyal var:{" "}
                {formatNumber(mobileSummary?.activeLast7 || 0)} cihaz {data.cutoff} altı sürümde görüldü,{" "}
                {formatNumber(legacySummary?.activeLast7 || 0)} cihaz eski uç noktalara yazdı.
              </>
            )}
          </div>

          <SectionCard title={`1. Sinyal: ${data.cutoff} Altı Sürümdeki Cihazlar (Mobile)`}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
              <StatCard label="Toplam Cihaz" value={mobileSummary.totalDevices} color="#1976d2" />
              <StatCard
                label="Son 7 Günde Görülen"
                value={mobileSummary.activeLast7}
                color={mobileSummary.activeLast7 > 0 ? "#e53935" : "#2e7d32"}
              />
              <StatCard
                label="Son 30 Günde Görülen"
                value={mobileSummary.activeLast30}
                color={mobileSummary.activeLast30 > 0 ? "#f9a825" : "#2e7d32"}
              />
              <StatCard label="30 Günden Eski" value={mobileSummary.older} sub="Muhtemelen terk edilmiş" color="#9e9e9e" />
              <StatCard
                label="Sürümü Bilinmeyen"
                value={mobileSummary.unknownVersionDevices}
                sub="appVersion bilgisi yok (cutoff dışı tutuldu)"
                color="#bdbdbd"
              />
            </div>

            <h4 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>
              Son 30 Günde Görülen Eski Sürüm Kullanıcıları (tip kırılımı)
            </h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
              <StatCard label="Veli" value={mobileSummary.usersActiveLast30?.parents} color="#7b1fa2" />
              <StatCard label="Personel" value={mobileSummary.usersActiveLast30?.personel} color="#8e24aa" />
              <StatCard label="Sürücü" value={mobileSummary.usersActiveLast30?.drivers} color="#ef6c00" />
              <StatCard label="Admin" value={mobileSummary.usersActiveLast30?.admins} color="#546e7a" />
              <StatCard label="Diğer" value={mobileSummary.usersActiveLast30?.other} color="#9e9e9e" />
            </div>

            {data.mobile.listCapped && (
              <p style={{ color: "#999", fontSize: "12px" }}>İlk 300 kayıt gösteriliyor (son görülme sırasına göre).</p>
            )}
            <SimpleTable
              columns={[
                { key: "version", label: "Sürüm", render: (row) => <Badge color="#ffebee">{row.version}</Badge> },
                { key: "lastSeen", label: "Son Görülme", render: (row) => formatDateTime(row.lastSeen) },
                { key: "phonenumber", label: "Telefon" },
                { key: "users", label: "Kullanıcılar", render: usersCell },
              ]}
              rows={data.mobile.devices}
              emptyText={`${data.cutoff} altı sürümde cihaz yok.`}
              maxHeight="450px"
              rowKey={(row, idx) => row.phonenumber + idx}
            />
          </SectionCard>

          <SectionCard title="2. Sinyal: Eski Uç Noktalara Yazan Cihazlar (UserPhone, son 30 gün)">
            <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
              UserPhone.lastlogin alanı son 30 günde güncellenen kayıtlar — yani eski uç noktaları hâlâ çağıran
              uygulamalar. Hangi sürümlerin yazdığını tablodan görebilirsiniz.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
              <StatCard
                label="Son 7 Günde Yazan"
                value={legacySummary.activeLast7}
                color={legacySummary.activeLast7 > 0 ? "#e53935" : "#2e7d32"}
              />
              <StatCard
                label="Son 30 Günde Yazan"
                value={legacySummary.activeLast30}
                color={legacySummary.activeLast30 > 0 ? "#f9a825" : "#2e7d32"}
              />
              <StatCard label="Veli" value={legacySummary.users?.parents} color="#7b1fa2" />
              <StatCard label="Personel" value={legacySummary.users?.personel} color="#8e24aa" />
              <StatCard label="Sürücü" value={legacySummary.users?.drivers} color="#ef6c00" />
            </div>

            {data.legacyEndpoint.listCapped && (
              <p style={{ color: "#999", fontSize: "12px" }}>İlk 300 kayıt gösteriliyor (son giriş sırasına göre).</p>
            )}
            <SimpleTable
              columns={[
                { key: "lastLogin", label: "Son Giriş" },
                { key: "version", label: "Sürüm", render: (row) => <Badge color="#fff3e0">{row.version}</Badge> },
                { key: "platform", label: "Platform" },
                { key: "name", label: "İsim" },
                {
                  key: "usertype",
                  label: "Tip",
                  render: (row) => <Badge>{userTypeLabel(row.usertype)}</Badge>,
                },
                { key: "company", label: "Şirket" },
                { key: "phonenumber", label: "Telefon" },
              ]}
              rows={data.legacyEndpoint.devices}
              emptyText="Son 30 günde eski uç noktalara yazan cihaz yok."
              maxHeight="450px"
              rowKey={(row, idx) => row.phonenumber + row.lastLogin + idx}
            />
          </SectionCard>
        </>
      ) : null}
    </div>
  );
};

export default MigrationSection;
